import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useToast } from '../contexts/ToastContext';
import { usePageTitle } from '../hooks/usePageTitle';
import LoadingSpinner from '../components/LoadingSpinner';

const TeamJoinPage: React.FC = () => {
  usePageTitle('Join Team');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, tokens } = useAuthStore();
  const { fetchCurrentSubscription } = useSubscriptionStore();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  useEffect(() => {
    const joinTeam = async () => {
      if (!token) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }

      // If not authenticated, show sign in/create account screen
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      // User is authenticated, proceed with joining
      try {
        const response = await fetch(`${API_BASE_URL}/teams/join/${token}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens?.access}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage = errorData.error || errorData.detail || 'Failed to join team';
          setError(errorMessage);
          setLoading(false);
          return;
        }

        const data = await response.json();
        showSuccess('Team Joined!', data.message || `Successfully joined the team.`);
        
        // Refresh subscription to update team limits
        await fetchCurrentSubscription();
        
        // Redirect to teams page after a short delay
        setTimeout(() => {
          navigate('/app/teams', { replace: true });
        }, 1500);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to join team';
        setError(errorMessage);
        setLoading(false);
      }
    };

    joinTeam();
  }, [token, isAuthenticated, tokens, navigate, showSuccess, showError, fetchCurrentSubscription, API_BASE_URL]);

  // Show sign in/create account screen if not authenticated
  if (!isAuthenticated && !loading && !error) {
    const redirectUrl = `/teams/join/${token}`;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Join Team</h2>
            <p className="text-gray-600">
              You need to sign in or create an account to join this team.
            </p>
          </div>
          
          <div className="space-y-3">
            <Link
              to={`/login?redirect=${encodeURIComponent(redirectUrl)}`}
              className="block w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
            >
              Sign In
            </Link>
            <Link
              to={`/register?redirect=${encodeURIComponent(redirectUrl)}`}
              className="block w-full py-3 bg-white hover:bg-gray-50 text-orange-500 font-semibold rounded-xl border-2 border-orange-500 transition-colors"
            >
              Create Account
            </Link>
          </div>
          
          <p className="mt-6 text-xs text-gray-500">
            Already have an account?{' '}
            <Link to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} className="text-orange-500 hover:text-orange-600 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Joining team...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 max-w-md w-full text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Join Team</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/app/teams')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Go to Teams
            </button>
            <button
              onClick={() => navigate('/app/dashboard')}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TeamJoinPage;

